import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { selectAccessToken } from '../../../slices/staffSlice';
import { show } from '../../../slices/toastSlice';
import { Topbar, Sidebar, Loader } from '../../../components';
import { HugeiconsIcon } from '@hugeicons/react';
import { AuditIcon, ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons';

// Admin > Audit Log: every change request made through auth, loan and payment
// services (who, what, which record, outcome, where from). Read-only.
const SERVICES = [['', 'All services'], ['auth', 'Auth / staff'], ['loan', 'Loans'], ['payment', 'Payments']];
const OUTCOMES = [['', 'Any outcome'], ['success', 'Succeeded'], ['failed', 'Failed']];

const formatTime = (t) => new Date(t).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'medium' });
// the original client address is the first entry of x-forwarded-for, when present
const sourceIp = (log) => String(log?.details?.forwardedFor || '').split(',')[0].trim() || log?.ipAddress || '-';

function AuditLog() {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [filters, setFilters] = useState({ service: '', actor: '', action: '', outcome: '', from: '', to: '' });
    const [applied, setApplied] = useState(filters);
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ logs: [], metadata: { totalCount: 0, totalPages: 0, currentPage: 1 } });
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = { page, limit: 25 };
        Object.entries(applied).forEach(([k, v]) => { if (v) params[k] = k === 'to' ? `${v}T23:59:59` : v; });
        const res = await fetchRequests(`/auth/audit-logs`, 'GET', authStaff(accessToken), params, {});
        setLoading(false);

        if (res?.code === 401) {
            sessionStorage.clear();
            navigate('/login');
            dispatch(show({ type: 'error', state: true, message: 'Your session has expired. Please login to continue.' }));
            return;
        }
        if (res?.err) {
            dispatch(show({ type: 'error', state: true, message: res.err }));
            return;
        }
        setData(res);
    }, [accessToken, applied, page, dispatch, navigate]);

    useEffect(() => { load(); }, [load]);

    const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });
    const apply = (e) => { e.preventDefault(); setPage(1); setApplied(filters); };
    const clear = () => { const empty = { service: '', actor: '', action: '', outcome: '', from: '', to: '' }; setFilters(empty); setApplied(empty); setPage(1); };

    const { logs, metadata } = data;

    return (
        <div className="main-container">
            <Topbar />
            <Sidebar />
            <main className="main-content">
                <section className="section-container space-y-5">

                    {/* Header */}
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#1e3a6e]/10 text-[#1e3a6e] dark:text-blue-400 flex items-center justify-center">
                            <HugeiconsIcon icon={AuditIcon} size={20} />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Audit Log</h1>
                            <p className="text-sm text-gray-400">Every change made in the system: who, what, when and from where</p>
                        </div>
                    </div>

                    {/* Filters */}
                    <form onSubmit={apply} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                        <select value={filters.service} onChange={set('service')} className="form-input">
                            {SERVICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <input value={filters.actor} onChange={set('actor')} placeholder="Who (email)" className="form-input" />
                        <input value={filters.action} onChange={set('action')} placeholder="Action, e.g. disburse" className="form-input" />
                        <select value={filters.outcome} onChange={set('outcome')} className="form-input">
                            {OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <input type="date" value={filters.from} onChange={set('from')} className="form-input" title="From" />
                        <input type="date" value={filters.to} onChange={set('to')} className="form-input" title="To" />
                        <div className="sm:col-span-2 lg:col-span-6 flex items-center gap-2">
                            <button type="submit" className="rounded-lg bg-[#1e3a6e] hover:bg-[#162c54] px-4 py-2 text-sm font-semibold text-white">Search</button>
                            <button type="button" onClick={clear} className="text-sm text-gray-500 hover:underline">Clear</button>
                            <span className="ml-auto text-xs text-gray-400">{metadata.totalCount} entr{metadata.totalCount === 1 ? 'y' : 'ies'}</span>
                        </div>
                    </form>

                    {/* Table */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm overflow-x-auto">
                        {loading ? (
                            <div className="flex items-center justify-center py-16"><Loader color="text-gray-400" size="w-6 h-6" /></div>
                        ) : logs.length === 0 ? (
                            <p className="py-16 text-center text-sm text-gray-400">No entries match these filters.</p>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                        <th className="px-4 py-3 font-medium">When</th>
                                        <th className="px-4 py-3 font-medium">Who</th>
                                        <th className="px-4 py-3 font-medium">Action</th>
                                        <th className="px-4 py-3 font-medium">Record</th>
                                        <th className="px-4 py-3 font-medium">Outcome</th>
                                        <th className="px-4 py-3 font-medium">IP</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log) => {
                                        const failed = log?.details?.outcome === 'failed';
                                        const isOpen = open === log._id;
                                        return [
                                            <tr key={log._id} onClick={() => setOpen(isOpen ? null : log._id)} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50/60 dark:hover:bg-gray-700/30 cursor-pointer align-top">
                                                <td className="px-4 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">{formatTime(log.timestamp)}</td>
                                                <td className="px-4 py-3">
                                                    <div className="font-medium text-gray-800 dark:text-gray-100">{log.actor?.email}</div>
                                                    {log.actor?.id === 'anonymous'
                                                        ? <div className="text-xs text-amber-600">not signed in</div>
                                                        : log.actor?.name && log.actor.name !== '-' && <div className="text-xs text-gray-400">{log.actor.name}</div>}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="font-mono text-xs text-gray-700 dark:text-gray-200">{log.action}</span>
                                                    <div className="text-xs text-gray-400">{log.resource}</div>
                                                </td>
                                                <td className="px-4 py-3 font-mono text-xs text-gray-500 break-all">{log.resourceId || '-'}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${failed ? 'bg-red-50 text-red-600 border-red-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100'}`}>
                                                        {failed ? `Failed (${log.details?.status})` : 'Succeeded'}
                                                    </span>
                                                    {log.details?.error && <div className="mt-1 text-xs text-gray-500 max-w-xs">{log.details.error}</div>}
                                                </td>
                                                <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">{sourceIp(log)}</td>
                                            </tr>,
                                            isOpen && (
                                                <tr key={`${log._id}-details`} className="bg-gray-50/60 dark:bg-gray-900/40">
                                                    <td colSpan={6} className="px-4 py-3">
                                                        <pre className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-all">{JSON.stringify({ fields: log.details?.fields, userAgent: log.details?.userAgent, forwardedFor: log.details?.forwardedFor, serverIp: log.ipAddress }, null, 2)}</pre>
                                                    </td>
                                                </tr>
                                            ),
                                        ];
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {metadata.totalPages > 1 && (
                        <div className="flex items-center justify-end gap-3 text-sm text-gray-500">
                            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40">
                                <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
                            </button>
                            <span>Page {metadata.currentPage} of {metadata.totalPages}</span>
                            <button type="button" disabled={page >= metadata.totalPages || loading} onClick={() => setPage(page + 1)} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40">
                                <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
                            </button>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
}

export default AuditLog;
